import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';

export interface TourStep {
  route: string;
  target: string;
  title: string;
  description: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    route: '/dashboard',
    target: '[data-tour="dashboard-overview"]',
    title: 'Your Command Center',
    description: 'Your dashboard shows account balance, P&L, and open positions at a glance. This is home base.',
  },
  {
    route: '/dashboard',
    target: '[data-tour="dashboard-strategies-card"]',
    title: 'Pre-loaded Strategies',
    description: "We've set up 3 paper-trading strategies for you to explore. No real money involved — just learning.",
  },
  {
    route: '/strategies',
    target: '[data-tour="strategies-table"]',
    title: 'Powered by Real Data',
    description: 'Quantato uses public data sources — weather forecasts, economic indicators, market prices, and bookmaker odds — to estimate probabilities. When data confidence is high, strategies skip expensive AI calls to save you money.',
  },
  {
    route: '/strategies',
    target: '[data-tour="strategies-table"]',
    title: 'Your Strategies',
    description: 'Here you can see all your strategies with their rules, risk settings, and performance stats.',
  },
  {
    route: '/strategies',
    target: '[data-tour="create-strategy-btn"]',
    title: 'Create Your Own',
    description: 'Describe your trading idea in plain English and our AI converts it into real trading rules.',
  },
  {
    route: '/strategies',
    target: '[data-tour="backtest-methodology"]',
    title: 'How We Test Strategies',
    description: 'Our testing engine runs your strategy 1,000 times against real market data, randomizing conditions each time. You get a range of outcomes — not just one guess. Learn more on the methodology page after creating your first strategy.',
  },
  {
    route: '/strategies',
    target: '[data-tour="create-strategy-btn"]',
    title: 'Test Before You Trade',
    description: 'Now try it — test any strategy against historical data before risking real capital.',
  },
  {
    route: '/strategies',
    target: '[data-tour="strategy-advisor-btn"]',
    title: 'Your AI Assistant',
    description: 'Ask questions about your strategies, review your data, or make configuration changes — all through conversation.',
  },
  {
    route: '',
    target: '[data-tour="help-chat-fab"]',
    title: 'Chat With Us Anytime',
    description: "Let's open the chat and walk you through what to do next!",
  },
];

interface OnboardingState {
  tourActive: boolean;
  tourStep: number;
  tourCompleted: boolean;
  isOnboardingChatActive: boolean;
  isLoading: boolean;
}

interface OnboardingContextType extends OnboardingState {
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  endTour: () => void;
  endOnboardingChat: () => void;
}

const OnboardingContext = createContext<OnboardingContextType>({
  tourActive: false,
  tourStep: 0,
  tourCompleted: false,
  isOnboardingChatActive: false,
  isLoading: true,
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  endTour: () => {},
  endOnboardingChat: () => {},
});

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<OnboardingState>({
    tourActive: false,
    tourStep: 0,
    tourCompleted: false,
    isOnboardingChatActive: false,
    isLoading: true,
  });

  // Check onboarding status on mount
  useEffect(() => {
    if (!user) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await api.get('/api/v1/support/onboarding-status');
        if (!res.data.has_seen_onboarding) {
          setState((s) => ({ ...s, tourActive: true, isLoading: false }));
        } else {
          setState((s) => ({ ...s, isLoading: false }));
        }
      } catch {
        setState((s) => ({ ...s, isLoading: false }));
      }
    };
    checkStatus();
  }, [user]);

  const navigateToStep = useCallback(
    (stepIndex: number) => {
      const step = TOUR_STEPS[stepIndex];
      if (!step) return;

      // If the step needs a specific route and we're not there, navigate
      if (step.route && location.pathname !== step.route) {
        navigate(step.route);
      }

      setState((s) => ({ ...s, tourStep: stepIndex }));
    },
    [navigate, location.pathname],
  );

  const nextStep = useCallback(() => {
    const next = state.tourStep + 1;
    if (next >= TOUR_STEPS.length) {
      // Tour is over — transition to onboarding chat
      setState((s) => ({
        ...s,
        tourActive: false,
        tourCompleted: true,
        isOnboardingChatActive: true,
      }));
    } else {
      navigateToStep(next);
    }
  }, [state.tourStep, navigateToStep]);

  const prevStep = useCallback(() => {
    if (state.tourStep > 0) {
      navigateToStep(state.tourStep - 1);
    }
  }, [state.tourStep, navigateToStep]);

  const skipTour = useCallback(async () => {
    setState((s) => ({
      ...s,
      tourActive: false,
      tourCompleted: true,
      isOnboardingChatActive: false,
    }));
    try {
      await api.post('/api/v1/support/onboarding-dismiss');
    } catch {
      // Non-critical
    }
  }, []);

  const endTour = useCallback(() => {
    setState((s) => ({
      ...s,
      tourActive: false,
      tourCompleted: true,
      isOnboardingChatActive: true,
    }));
  }, []);

  const endOnboardingChat = useCallback(async () => {
    setState((s) => ({ ...s, isOnboardingChatActive: false }));
    try {
      await api.post('/api/v1/support/onboarding-dismiss');
    } catch {
      // Non-critical
    }
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        ...state,
        nextStep,
        prevStep,
        skipTour,
        endTour,
        endOnboardingChat,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => useContext(OnboardingContext);
